import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { apiHelpers } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { CustomerCreatedPrompt } from '@/components/WhatsNextPrompt';
import { PermissionGate, usePermission } from '@/components/PermissionGate';
import { 
  Users, 
  Plus, 
  Search, 
  Phone, 
  Mail, 
  MapPin,
  ChevronRight,
  UserPlus,
  Download,
  Zap
} from 'lucide-react';

const ClientCard = ({ client, onClick }) => {
  const { t } = useLanguage();
  const hasNoContact = !client.email && !client.phone;
  
  return (
    <button
      onClick={onClick}
      className="card-default w-full text-left group"
      data-testid={`client-card-${client.id}`}
    >
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-primary font-bold text-xl flex-shrink-0 relative">
          {client.name.charAt(0).toUpperCase()}
          {hasNoContact && (
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center" title={t('no_contact_info')}>
              <span className="text-white text-[10px] font-bold">!</span>
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-heading text-lg font-semibold truncate group-hover:text-primary transition-colors">
            {client.name}
          </h3>
          {client.email ? (
            <p className="text-muted-foreground text-sm truncate flex items-center gap-1">
              <Mail className="w-3 h-3" />
              {client.email}
            </p>
          ) : !client.phone ? (
            <p className="text-amber-600 dark:text-amber-400 text-sm flex items-center gap-1">
              <Mail className="w-3 h-3" />
              <span className="italic">{t('no_email')}</span>
            </p>
          ) : null}
          {client.phone ? (
            <p className="text-muted-foreground text-sm flex items-center gap-1">
              <Phone className="w-3 h-3" />
              {client.phone}
            </p>
          ) : client.email ? (
            <p className="text-amber-600 dark:text-amber-400 text-sm flex items-center gap-1">
              <Phone className="w-3 h-3" />
              <span className="italic">{t('no_phone')}</span>
            </p>
          ) : null}
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>
    </button>
  );
};

const AddClientDialog = ({ open, onOpenChange, onClientAdded, t, quickAddMode = false }) => {
  const { organization, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isQuickAdd, setIsQuickAdd] = useState(quickAddMode);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    notes: ''
  });
  const [errors, setErrors] = useState({});

  // Reset form when dialog opens/closes
  const handleOpenChange = (isOpen) => {
    if (!isOpen) {
      setFormData({ name: '', email: '', phone: '', address: '', notes: '' });
      setErrors({});
      setIsQuickAdd(quickAddMode);
    }
    onOpenChange(isOpen);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    // Validate email format only if provided
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Show toast for missing required fields
      if (errors.name || !formData.name.trim()) {
        toast.error('Name is required');
        return;
      }
      if (errors.address || !formData.address.trim()) {
        toast.error('Address is required');
        return;
      }
      return;
    }
    
    setIsLoading(true);
    try {
      // Clean data - don't send empty strings for optional fields
      const cleanedData = {
        name: formData.name.trim(),
        address: formData.address.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        notes: formData.notes.trim() || null
      };
      
      const response = await apiHelpers.createClient(
        organization.id,
        cleanedData,
        user.id,
        user.name
      );
      
      // Show warning if no contact info
      if (!cleanedData.email && !cleanedData.phone) {
        toast.success(t('customer_added'), { 
          description: `${formData.name} - No contact info provided`
        });
      } else {
        toast.success(t('customer_added'), { description: `${formData.name}` });
      }
      
      onClientAdded(response.data);
      handleOpenChange(false);
    } catch (error) {
      console.error('Failed to add customer:', error);
      // Better error message handling
      let errorMessage = 'Failed to add customer';
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg).join(', ');
        } else {
          errorMessage = error.response.data.detail;
        }
      }
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const isFormValid = formData.name.trim() && formData.address.trim();

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl sm:text-2xl">
            {isQuickAdd ? 'Quick Add Customer' : t('add_new_customer')}
          </DialogTitle>
          {/* Toggle between Quick Add and Full Form */}
          <div className="flex gap-2 mt-2">
            <Button
              type="button"
              variant={isQuickAdd ? "default" : "outline"}
              size="sm"
              onClick={() => setIsQuickAdd(true)}
              className={isQuickAdd ? "bg-primary text-primary-foreground" : ""}
              data-testid="quick-add-toggle"
            >
              Quick Add
            </Button>
            <Button
              type="button"
              variant={!isQuickAdd ? "default" : "outline"}
              size="sm"
              onClick={() => setIsQuickAdd(false)}
              className={!isQuickAdd ? "bg-primary text-primary-foreground" : ""}
              data-testid="full-form-toggle"
            >
              Full Details
            </Button>
          </div>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
          {/* Name - Always Required */}
          <div>
            <Label htmlFor="clientName" className="text-sm sm:text-base">{t('name')} *</Label>
            <Input
              id="clientName"
              value={formData.name}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, name: e.target.value }));
                if (errors.name) setErrors(prev => ({ ...prev, name: null }));
              }}
              placeholder="John Smith"
              className={`input-default mt-1 h-10 sm:h-11 ${errors.name ? 'border-red-500' : ''}`}
              data-testid="add-client-name"
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>

          {/* Address - Always Required */}
          <div>
            <Label htmlFor="clientAddress" className="text-sm sm:text-base">{t('address')} *</Label>
            <Input
              id="clientAddress"
              value={formData.address}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, address: e.target.value }));
                if (errors.address) setErrors(prev => ({ ...prev, address: null }));
              }}
              placeholder="123 Main St, City, State"
              className={`input-default mt-1 h-10 sm:h-11 ${errors.address ? 'border-red-500' : ''}`}
              data-testid="add-client-address"
            />
            {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
          </div>
          
          {/* Full form fields - only show when not in Quick Add mode */}
          {!isQuickAdd && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <Label htmlFor="clientEmail" className="text-sm sm:text-base flex items-center gap-1">
                    {t('email')}
                    <span className="text-xs text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    id="clientEmail"
                    type="email"
                    value={formData.email}
                    onChange={(e) => {
                      setFormData(prev => ({ ...prev, email: e.target.value }));
                      if (errors.email) setErrors(prev => ({ ...prev, email: null }));
                    }}
                    placeholder="john@email.com"
                    className={`input-default mt-1 h-10 sm:h-11 ${errors.email ? 'border-red-500' : ''}`}
                    data-testid="add-client-email"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="clientPhone" className="text-sm sm:text-base flex items-center gap-1">
                    {t('phone')}
                    <span className="text-xs text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    id="clientPhone"
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="input-default mt-1 h-10 sm:h-11"
                    data-testid="add-client-phone"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="clientNotes" className="text-sm sm:text-base">{t('notes')}</Label>
                <textarea
                  id="clientNotes"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder=""
                  className="input-default mt-1 w-full min-h-[60px] sm:min-h-[80px] py-2 sm:py-3 text-sm"
                  data-testid="add-client-notes"
                />
              </div>
            </>
          )}

          {/* Info message for Quick Add mode */}
          {isQuickAdd && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-700 dark:text-blue-300">
              Quick Add creates a customer with just name and address. You can add email and phone later from the customer profile.
            </div>
          )}
          
          <div className="flex justify-end gap-2 sm:gap-3 pt-3 sm:pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              className="h-10 sm:h-12 px-4 sm:px-6 text-sm"
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !isFormValid}
              className="btn-primary h-10 sm:h-12 px-4 sm:px-6 text-sm"
              data-testid="add-client-submit"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  {t('adding')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4 sm:w-5 sm:h-5" />
                  {isQuickAdd ? 'Quick Add' : t('add_customer_btn')}
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ClientsPage = () => {
  const { organization } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const actionParam = searchParams.get('action');
  
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showQuickAddDialog, setShowQuickAddDialog] = useState(false);
  const [showWhatsNext, setShowWhatsNext] = useState(false);
  const [newlyCreatedClient, setNewlyCreatedClient] = useState(null);

  // Check for action=new to auto-open modal
  useEffect(() => {
    if (actionParam === 'new') {
      setShowAddDialog(true);
      setSearchParams({});
    }
  }, [actionParam, setSearchParams]);

  useEffect(() => {
    loadClients();
  }, [organization?.id]);

  const loadClients = async (search = '') => {
    if (!organization?.id) return;
    
    try {
      setIsLoading(true);
      const response = await apiHelpers.getClients(organization.id, search);
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients:', error);
      toast.error(t('failed_to_load'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    // Debounce search
    const timeoutId = setTimeout(() => {
      loadClients(query);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  };

  const handleClientAdded = (newClient) => {
    setClients(prev => [newClient, ...prev]);
    setNewlyCreatedClient(newClient);
    setShowWhatsNext(true);
  };

  const handleScheduleVisit = () => {
    if (newlyCreatedClient) {
      navigate(`/site-visits?action=new&client_id=${newlyCreatedClient.id}`);
    }
  };

  const handleCreateEstimate = () => {
    if (newlyCreatedClient) {
      navigate(`/estimates/new?client_id=${newlyCreatedClient.id}`);
    }
  };

  const handleExportCSV = async () => {
    try {
      const response = await apiHelpers.exportClients(organization.id);
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clients-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Clients exported to CSV');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export clients');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground tracking-tight">
              {t('customers_title')}
            </h1>
            <p className="text-muted-foreground text-base md:text-lg mt-1">
              {clients.length} {t('total_customers')}
            </p>
          </div>
          
          {/* Action buttons - wrap on mobile */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={handleExportCSV}
              data-testid="export-clients-csv-btn"
              size="sm"
              className="h-9"
            >
              <Download className="w-4 h-4 mr-1.5" />
              <span className="hidden xs:inline">{t('export')}</span>
              <span className="xs:hidden">CSV</span>
            </Button>
            <PermissionGate permission="create_customers">
              <Button
                variant="outline"
                onClick={() => setShowQuickAddDialog(true)}
                data-testid="quick-add-client-btn"
                size="sm"
                className="h-9"
              >
                <Zap className="w-4 h-4 mr-1.5" />
                Quick
              </Button>
              <Button
                onClick={() => setShowAddDialog(true)}
                className="btn-primary h-9"
                data-testid="add-client-btn"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={2.5} />
                <span className="hidden sm:inline">{t('add_customer_btn')}</span>
                <span className="sm:hidden">{t('add')}</span>
              </Button>
            </PermissionGate>
          </div>
        </div>
      </div>
      
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={handleSearch}
          placeholder={t('search_placeholder')}
          className="input-default pl-12"
          data-testid="client-search-input"
        />
      </div>
      
      {/* Customers List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : clients.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {clients.map((client) => (
            <ClientCard
              key={client.id}
              client={client}
              onClick={() => navigate(`/clients/${client.id}`)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 card-default">
          <Users className="w-16 h-16 text-muted-foreground mx-auto mb-4 opacity-50" />
          <h3 className="font-heading text-xl font-semibold mb-2">
            {searchQuery ? t('no_customers_found') : t('no_customers_yet')}
          </h3>
          <p className="text-muted-foreground mb-6">
            {searchQuery 
              ? t('try_different_search') 
              : t('add_first_desc')}
          </p>
          {!searchQuery && (
            <PermissionGate permission="create_customers">
              <Button
                onClick={() => setShowAddDialog(true)}
                className="btn-primary"
              >
                <Plus className="w-5 h-5 mr-2" strokeWidth={2.5} />
                {t('add_first_customer')}
              </Button>
            </PermissionGate>
          )}
        </div>
      )}
      
      {/* Add Customer Dialog */}
      <AddClientDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onClientAdded={handleClientAdded}
        t={t}
      />

      {/* Quick Add Customer Dialog */}
      <AddClientDialog
        open={showQuickAddDialog}
        onOpenChange={setShowQuickAddDialog}
        onClientAdded={handleClientAdded}
        t={t}
        quickAddMode={true}
      />
      
      {/* What's Next Prompt */}
      <CustomerCreatedPrompt
        open={showWhatsNext}
        onOpenChange={setShowWhatsNext}
        customerName={newlyCreatedClient?.name || ''}
        onScheduleVisit={handleScheduleVisit}
        onCreateEstimate={handleCreateEstimate}
      />
    </div>
  );
};

export default ClientsPage;
